import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { InvoiceCard, SearchView } from "../../../components";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { countryOptions } from "@/utils/country-options";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import {
  getMyPaymentsCommissions,
  downloadMyPaymentsCommissionsExcel,
} from "@/data/requests/dashboard-requests";

const STATUS_OPTIONS = [
  { value: "Invoice Uploaded", label: "Invoice Uploaded" },
  { value: "Mail Sended", label: "Mail Sended" },
  { value: "Auto Created Invoice", label: "Auto Created Invoice" },
  { value: "OCR Completed", label: "OCR Completed" },
  { value: "all", label: "All" },
];

export function MyPaymentsCommissionsScreen() {
  const { isAdvancedTable } = useTableView();
  const { toast } = useToast();
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [pageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    name: "",
    email: "",
    status: "",
    country: "",
  });

  const navigate = useNavigate();

  const [page, setPage] = useState(() => {
    const storedPage = localStorage.getItem(`myPaymentsCommissionsPage`);
    return storedPage ? parseInt(storedPage, 10) : 1;
  });
  const [queryParams, setQueryParams] = useState(() => {
    const storedQueryParams = localStorage.getItem(
      `myPaymentsCommissionsQueryParams`
    );
    if (storedQueryParams && storedQueryParams !== "{}") {
      setSearchParams(JSON.parse(storedQueryParams));
    }
    return storedQueryParams ? JSON.parse(storedQueryParams) : {};
  });

  useEffect(() => {
    localStorage.setItem(`myPaymentsCommissionsPage`, page.toString());
    localStorage.setItem(
      `myPaymentsCommissionsQueryParams`,
      JSON.stringify(queryParams)
    );
  }, [page, queryParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "my-payments-commissions",
      page,
      pageSize,
      queryParams,
      sortColumn,
      sortDirection,
    ],
    queryFn: () =>
      getMyPaymentsCommissions({
        page,
        pageSize,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        statusFilter: queryParams.status,
        countryFilter: queryParams.country,
        sortColumn,
        sortDirection,
      }),
    onError: (error) => {
      toast({
        title: "Error loading commissions",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  const downloadExcel = () => {
    downloadMyPaymentsCommissionsExcel({
      nameFilter: queryParams.name,
      emailNameFilter: queryParams.email,
      statusFilter: queryParams.status,
      countryFilter: queryParams.country,
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "my-commissions.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd.MM.yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const pageSettings = {
    title: "Benim Payment'larım - Commissions",
    actions: [
      {
        name: "Download Excel",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        onClick: downloadExcel,
      },
    ],
  };

  // Simple columns for basic table
  const simpleColumns = [
    {
      key: "influencerName",
      label: "Name",
      accessor: (item) => item.influencerName || "",
    },
    {
      key: "email",
      label: "Email",
      accessor: (item) => item.email || "",
    },
    {
      key: "paymentName",
      label: "Payment",
      accessor: (item) => item.payment?.name || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Account Name",
      accessor: (item) => item.Client?.lucaAccountName || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      accessor: (item) => item.Client?.lucaId || "",
    },
    {
      key: "finalAmount",
      label: "Final Amount",
      accessor: (item) => item.finalAmount || "",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.status}</span>
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => <div className="flex gap-2"></div>,
    },
  ];

  // Advanced columns for advanced table
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    { key: "influencerName", label: "Influencer Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    {
      key: "paymentName",
      label: "Payment",
      sortable: false,
      accessor: (item) => item.payment?.name || "",
    },
    {
      key: "paymentInvoiceId",
      label: "Payment Invoice ID",
      sortable: false,
      accessor: (item) => item.payment?.invoiceId || "",
    },
    { key: "totalEarnings", label: "Total Earnings", sortable: true },
    { key: "ourCommission", label: "Our Commission", sortable: true },
    { key: "finalAmount", label: "Final Amount", sortable: true },
    { key: "invoiceId", label: "Invoice ID", sortable: true },
    {
      key: "isMailSended",
      label: "Mail Sent",
      sortable: true,
      accessor: (item) => (item.isMailSended ? "Yes" : "No"),
    },
    { key: "status", label: "Status", sortable: true },
    { key: "managerName", label: "Manager", sortable: true },
    { key: "country", label: "Country", sortable: true },
    {
      key: "minAcceptedInvoiceDate",
      label: "Min Invoice Date",
      sortable: true,
      accessor: (item) =>
        item.minAcceptedInvoiceDate
          ? new Date(item.minAcceptedInvoiceDate).toLocaleDateString()
          : "-",
    },
    {
      key: "clientName",
      label: "Client Name",
      sortable: false,
      accessor: (item) => item.Client?.name || "",
    },
    {
      key: "clientEmail",
      label: "Client Email",
      sortable: false,
      accessor: (item) => item.Client?.email || "",
    },
    {
      key: "billingName",
      label: "Billing Name",
      sortable: false,
      accessor: (item) => item.Client?.billingName || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      sortable: false,
      accessor: (item) => item.Client?.lucaId || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Account Name",
      sortable: false,
      accessor: (item) => item.Client?.lucaAccountName || "",
    },
    {
      key: "clientCurrency",
      label: "Client Currency",
      sortable: false,
      accessor: (item) => item.Client?.currency || "",
    },
    {
      key: "clientBankName",
      label: "Client Bank",
      sortable: false,
      accessor: (item) => item.Client?.bankName || "",
    },
    {
      key: "clientIBAN",
      label: "Client IBAN",
      sortable: false,
      accessor: (item) => item.Client?.IBAN || "",
    },
    {
      key: "clientSWIFT",
      label: "Client SWIFT",
      sortable: false,
      accessor: (item) => item.Client?.SWIFT || "",
    },
    {
      key: "newTvAccountManager",
      label: "NewTV Manager",
      sortable: false,
      accessor: (item) => item.Client?.newTvAccountManager || "",
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      accessor: (item) =>
        item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-",
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() =>
              navigate(`/payments/${row.paymentId}/commissions/${row.id}`)
            }
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = [
    "influencerName",
    "email",
    "paymentName",
    "finalAmount",
    "status",
    "country",
    "lucaId",
    "actions",
  ];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
      style={{ padding: "0 10px" }}
    >
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        countryOptions={countryOptions}
        resetDefault={() => {
          setSearchParams({
            name: "",
            email: "",
            status: "",
            country: "",
          });
          setQueryParams({
            name: "",
            email: "",
            status: "",
            country: "",
          });
          setPage(1);
        }}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissions}
          columns={advancedColumns}
          columnVisibilityKey="my-payments-commissions-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(`/payments/${item.paymentId}/commissions/${item.id}`)
          }
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissions}
          columns={simpleColumns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(`/payments/${item.paymentId}/commissions/${item.id}`)
          }
          renderGridItem={(item) => (
            <InvoiceCard
              key={item.id}
              name={item.Client?.name}
              email={item.email}
              invoiceId={item.invoiceId}
              finalAmount={item.finalAmount}
              status={item.status}
              onDetailClick={() =>
                navigate(`/payments/${item.paymentId}/commissions/${item.id}`)
              }
            />
          )}
        />
      )}
    </AppLayout>
  );
}
